import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  Tabs,
  Tab
} from '@mui/material';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import UserMoreMenu from '../../components/_dashboard/user/UserMoreMenu';
import UserListHead from '../../components/_dashboard/user/UserListHead';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import partnerService from '../services/partnerService';
import { fDate } from '../../utils/formatTime';
// styling
import { StyledTableRow, StyledTableCell } from '../Global/Styling';
import OnBoardingToolbar from './OnBoardingToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getOnboardingsList } from '../../store/Slice/Onboarding/OnboardingSlice';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';
import PartnerHierarchyPage from './PartnerHierarchyComponent';

// ----------------------------------------------------------------------
const { UpdatePartnerStatus, PartnerStatusTypes } = partnerService;
const TABLE_HEAD = [
  { id: 'id', label: 'Partner ID', alignRight: false },
  { id: 'partnerName', label: 'Partner Name', alignRight: false },
  { id: 'partnerType', label: 'Partnery Type', alignRight: false },
  { id: 'city', label: 'Location', alignRight: false },
  { id: 'randomPhoneNumber', label: 'Mobile', alignRight: false },
  { id: 'created', label: 'Date Of Birth', alignRight: false },
  { id: 'active', label: 'Status', alignRight: false },
  { id: '', label: 'ACTION' }
];

export default function OnBoarding() {
  const pageCode = 'ONBOARDING_PAGE';
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageMenu, setPageMenu] = useState([]); // State for store localstorage data so that we can provide data to others components
  const [statusOptions, setStatusOptions] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const [filterState, setFilterState] = useState({
    typeId: null,
    partnerStatusTypeId: null,
    cityId: null,
    pageSize: 10,
    pageNumber: 1,
    sortOrder: 'desc'
  });

  const [hasNextPage, setHasNextPage] = useState(true);
  const { OnboardingListData } = useOnboardingSelector();

  useEffect(() => {
    if (!OnboardingListData) {
      setHasNextPage(true);
      return;
    }
    // If API returned fewer rows → last page
    setHasNextPage(OnboardingListData.length >= rowsPerPage);
  }, [OnboardingListData, rowsPerPage]);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const { AccessLevelConfig } = useGlobalSelector(); // sidebar data

  // loan type & Bank Dropdown
  const getOnboardingListAPi = useCallback(() => {
    if (!LoginData) return;

    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : '',
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: filterState?.sortOrder || 'desc'
      },
      filterModel: {
        typeId: filterState?.typeId ?? null,
        partnerStatusTypeId: filterState?.partnerStatusTypeId ?? null,
        cityId: filterState?.cityId ?? null
      }
    };

    dispatch(getOnboardingsList(data));
  }, [LoginData, debouncedSearch, filterState, dispatch, page, rowsPerPage]);

  useEffect(() => {
    getOnboardingListAPi();
  }, [getOnboardingListAPi]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if (Array.isArray(OnboardingListData)) {
      setStatusOptions((prev) => {
        const map = new Map();

        // keep existing options
        prev.forEach((opt) => {
          map.set(opt.value, opt);
        });

        // add from current grid data
        OnboardingListData.forEach((partner) => {
          const status = partner?.partnerStatus;
          const value = status?.typeId;
          const label = status?.typeName;

          if (value && label && !map.has(value)) {
            map.set(value, { value, label });
          }
        });

        return Array.from(map.values());
      });
    }
  }, [OnboardingListData]);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = OnboardingListData?.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setFilterState((prev) => ({ ...prev, pageNumber: newPage + 1 })); // backend pages are 1-based
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    setFilterState((prev) => ({ ...prev, pageSize: newSize, pageNumber: 1 }));
  };

  const handleClick = (event, partner) => {
    const selectedIndex = selected.indexOf(partner);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, partner);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const emptyRows = !hasNextPage ? Math.max(0, rowsPerPage - (OnboardingListData?.length ?? 0)) : 0;

  const UpdatePartnerStatusApi = async (rowId, partner) => {
    // console.log('sss rowid', rowId);
    // console.log('sss partner', partner);
    const PartnerStatusTypesApi = await PartnerStatusTypes(LoginData?.jwt);
    if (PartnerStatusTypesApi.status === 'success') {
      let partnerStatusTypeId = '';
      PartnerStatusTypesApi.data.partnerStatusTypes.forEach((item, index) => {
        if (item.typeName === 'Activated') {
          partnerStatusTypeId = item.typeId;
        }
      });

      const statusCredential = {
        companyId: partner.companyId,
        partnerId: rowId,
        partnerStatusTypeId: partnerStatusTypeId
      };
      const UpdatePartnerStatusResponse = await UpdatePartnerStatus(
        statusCredential,
        LoginData?.jwt
      );
      if (UpdatePartnerStatusResponse.status === 'success') {
        getOnboardingListAPi();
      }
    }
  };

  useEffect(() => {
    if (AccessLevelConfig) {
      AccessLevelConfig?.accessPagesMapping?.forEach((accessPageMap) => {
        if (accessPageMap?.pageCode === pageCode) {
          setPageMenu(accessPageMap?.accessSubPagesMappings);
        }
      });
    }
  }, [AccessLevelConfig]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <Page title="Kpros by Kalolytic Solutions || On Boarding">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="On Board Your Partner" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            className="tabOuter"
            aria-label="basic tabs example"
            classes={{ indicator: 'tabIndicator' }}
          >
            <Tab
              label="Partner"
              {...a11yProps(0)}
              classes={{ selected: 'tabSelected', root: 'tabDefault' }}
            />
            {/* <Tab
                          label="Partner"
                          {...a11yProps(1)}
                          classes={{ selected: 'tabSelected', root: 'tabDefault' }}
                          onClick={() => setTypeIdValue(302)}
                        /> */}
            <Tab
              label="Hierarchy"
              {...a11yProps(1)}
              classes={{ selected: 'tabSelected', root: 'tabDefault' }}
              disabled={selected?.length !== 1}
            />
          </Tabs>
          {/* {console.log('sss selected', selected)} */}
          <TabPanel value={tabValue} index={0}>
            <Card>
              <OnBoardingToolbar
                parent="onboarding"
                pageMenu={pageMenu}
                filterName={filterName}
                setFilterName={setFilterName}
                filterState={filterState}
                setFilterState={setFilterState}
                statusOptions={statusOptions}
              />

              <Scrollbar>
                {OnboardingListData && OnboardingListData?.length > 0 && (
                  <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
                    <Table>
                      <UserListHead
                        order={order}
                        orderBy={orderBy}
                        headLabel={TABLE_HEAD}
                        rowCount={OnboardingListData?.length}
                        numSelected={selected.length}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAllClick}
                        isCheckBox="true"
                      />
                      {console.log('sss OnboardingListData', OnboardingListData)}
                      <TableBody>
                        {Array.isArray(OnboardingListData) &&
                          OnboardingListData?.map((partner, index) => {
                            const isItemSelected = selected.indexOf(partner) !== -1;
                            return (
                              <StyledTableRow
                                hover
                                key={index}
                                tabIndex={-1}
                                role="checkbox"
                                selected={isItemSelected}
                                aria-checked={isItemSelected}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isItemSelected}
                                    onChange={(event) => handleClick(event, partner)}
                                  />
                                </TableCell>

                                <StyledTableCell align="left">
                                  {partner.partnerState === null ||
                                  partner?.partnerStatus?.typeName === 'Rejected' ||
                                  partner?.partnerStatus?.typeName === 'Approved' ||
                                  partner?.partnerStatus?.typeName === 'Inactive'
                                    ? ''
                                    : partner?.partnerIdToDisplay}
                                </StyledTableCell>
                                <StyledTableCell align="left">
                                  {partner?.partnerName}
                                </StyledTableCell>
                                <StyledTableCell align="left">
                                  {partner?.partnerType?.typeName}
                                </StyledTableCell>
                                <StyledTableCell align="left">
                                  {partner?.cityEntity !== null
                                    ? partner?.cityEntity?.cityName
                                    : 'N/A'}
                                </StyledTableCell>
                                <StyledTableCell align="left">{partner.mobile}</StyledTableCell>
                                <StyledTableCell align="left">{fDate(partner.dob)}</StyledTableCell>
                                <StyledTableCell align="left">
                                  {partner.partnerStatus !== null ? (
                                    <Label
                                      variant="ghost"
                                      color={
                                        (partner.partnerStatus.typeName === 'Rejected' &&
                                          'error') ||
                                        (partner.partnerStatus.typeName === 'Approved' &&
                                          'warning') ||
                                        (partner.partnerStatus.typeName === 'Activated' &&
                                          'success') ||
                                        (partner.partnerStatus.typeName === 'Inactive' &&
                                          'greymsg') ||
                                        'warning'
                                      }
                                    >
                                      {partner.partnerStatus.typeName}
                                    </Label>
                                  ) : (
                                    'No status'
                                  )}
                                </StyledTableCell>

                                <StyledTableCell align="" width={115}>
                                  {console.log('partnerpartnerpartner', partner)}
                                  <UserMoreMenu
                                    partner={partner}
                                    rowId={partner.partnerId}
                                    parent="on-boarding"
                                    UpdatePartnerStatusApi={UpdatePartnerStatusApi}
                                    pageMenu={pageMenu}
                                    partnerState={partner.partnerStatus}
                                  />
                                </StyledTableCell>
                              </StyledTableRow>
                            );
                          })}
                        {emptyRows > 0 && (
                          <TableRow style={{ height: 53 * emptyRows }}>
                            <StyledTableCell colSpan={6} />
                          </TableRow>
                        )}
                      </TableBody>
                      {OnboardingListData?.length === 0 && (
                        <TableBody>
                          <TableRow>
                            <StyledTableCell
                              align="center"
                              colSpan={TABLE_HEAD.length + 1}
                              sx={{ py: 3 }}
                            >
                              <SearchNotFound searchQuery={filterName} />
                            </StyledTableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                )}
              </Scrollbar>
              <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />

              {/* <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={OnboardingListData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            /> */}
            </Card>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PartnerHierarchyPage partnerId={selected[0]?.partnerId} />
          </TabPanel>
        </Box>
      </Container>
    </Page>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ paddingTop: '1rem' }}>
          <Card>{children}</Card>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}
